import { SITE } from "../config/site";

// Public policy pages. Plain-language, no invented regulatory claims.
// Each entry: { eyebrow, title, intro, sections: [{heading, body: [paragraphs] | bullets}] }

export const RISK_DISCLOSURE = {
  eyebrow: "Legal",
  title: "Risk Disclosure",
  intro:
    "Understanding risk is a condition of using this website. This page summarises the principal risks across Kalki AI Fund's four offerings. It is a summary, not an exhaustive list.",
  sections: [
    {
      heading: "General market risk",
      body: [
        "Digital assets are volatile. Prices can move sharply in short periods, and losses can be substantial and permanent. No offering described on this website — Master Trading, Strategy Trading, Vault Trading or Vault Yield — is suitable for a person who cannot absorb a total loss of committed capital.",
        "Past performance, whether live, backtested, simulated, audited or independently verified, is not indicative of future results. No statement on this website is a guarantee of return, repayment or redemption.",
      ],
    },
    {
      heading: "Model and methodology risk",
      body: [
        "Algorithmic strategies operate on models. Models can be wrong, can break under regime change, and can behave unpredictably in extreme market conditions. Backtesting is inherently limited: it cannot fully reproduce fees, slippage, liquidity and execution realities.",
      ],
    },
    {
      heading: "Custody and counterparty risk",
      body: [
        "Our offerings differ in custody structure, and the distinction matters:",
        "Master Trading and Strategy Trading are non-custodial — assets remain in your own exchange account or are executed through your own API keys. However, they remain exposed to the exchange as counterparty.",
        "Vault Trading and Vault Yield are custodial — participating assets are held under a custodial arrangement with the exchange, host platform or issuer. You bear the creditworthiness and operational soundness of that custodian or issuer, including in insolvency scenarios.",
        "Vault Yield additionally carries issuer default risk: coupons and redemption depend on the issuer's ability to perform.",
      ],
    },
    {
      heading: "Liquidity risk",
      body: [
        "Some vault and yield products have lock-in windows, maturity dates or redemption notice periods. During those periods, exit may be restricted or unavailable. Terms are stated in each product's documentation.",
      ],
    },
    {
      heading: "No advice",
      body: [
        "Nothing on this website constitutes investment, legal, accounting or tax advice, or an offer or solicitation in any jurisdiction. Seek independent professional advice before participating in any product described here.",
      ],
    },
  ],
};

export const PRIVACY_POLICY = {
  eyebrow: "Legal",
  title: "Privacy Policy",
  intro:
    "This website is a public marketing site. We collect the minimum information needed to respond to enquiries, and we never ask for exchange API keys, wallet private keys or financial account credentials.",
  sections: [
    {
      heading: "What we collect",
      body: [
        "Through the enquiry form we collect only: your name, email address, subject and message. These are stored securely and used solely to respond to your enquiry.",
        "We do not collect, and will never request: exchange API keys, wallet private keys or seed phrases, financial account credentials, or sensitive investment information.",
      ],
    },
    {
      heading: "How information is used",
      body: [
        "Enquiry data is used to answer your question and, where you have asked, to share information about the offering you enquired about. We do not sell personal data.",
      ],
    },
    {
      heading: "Analytics",
      body: [
        "This website uses privacy-conscious analytics to understand aggregate usage patterns (for example, which pages are visited). This data is not used to profile individuals.",
      ],
    },
    {
      heading: "Your choices",
      body: [
        `You may request access to, correction of, or deletion of your enquiry data at any time by writing to us through the Contact page or at ${SITE.contactEmail}.`,
      ],
    },
  ],
};

export const TERMS = {
  eyebrow: "Legal",
  title: "Terms and Conditions",
  intro:
    "These terms govern your use of this website. By browsing kalkiai.fund you accept them.",
  sections: [
    {
      heading: "Purpose of this website",
      body: [
        "This website is a public marketing and information resource. It does not provide a trading platform, account access, wallet, deposit or withdrawal functionality, and it does not execute transactions of any kind.",
      ],
    },
    {
      heading: "No offer, no advice",
      body: [
        "Nothing on this website is an offer to sell or a solicitation to buy any security, token, or financial product, nor investment advice. Availability of any product is subject to jurisdiction-specific rules and eligibility criteria stated in the relevant offering documents.",
      ],
    },
    {
      heading: "External links",
      body: [
        "Links to external exchanges, master trading pages and verification reports are provided for convenience. We do not control those destinations and are not responsible for their content, availability or terms.",
      ],
    },
    {
      heading: "Intellectual property",
      body: [
        "The Kalki AI Fund name, logo, and the content of this website are the property of their owner. Do not reproduce them without permission.",
      ],
    },
    {
      heading: "Changes",
      body: [
        "These terms may be updated. The version published on this page, with its last-updated date, is the version in force.",
      ],
    },
  ],
};

export const PERFORMANCE_METHODOLOGY = {
  eyebrow: "Transparency",
  title: "Performance Methodology",
  intro:
    "How performance information is labelled, sourced and presented across this website — and what we do when verified data is not yet available.",
  sections: [
    {
      heading: "Labelling standard",
      bullets: [
        "Backtested — simulated on historical data; hypothetical results have inherent limitations.",
        "Simulated — forward-tested in a non-live environment; not live capital.",
        "Live — achieved with live capital on the stated platform and period.",
        "Audited — reviewed by an independent auditor against underlying records.",
        "Independently verified — confirmed by a third party that did not issue the product.",
      ],
    },
    {
      heading: "What is shown for verified data",
      body: [
        "Where verified data exists, we present: historical returns, maximum drawdown, reporting period, realised performance, relevant risk metrics, fees and expenses, the data source, and the date the information was last updated.",
      ],
    },
    {
      heading: "When data is not available",
      body: [
        "We display an informational placeholder instead of numbers. We do not estimate, extrapolate, or combine different performance categories (for example, backtested with live). Where a figure depends on confirmation by a provider or auditor, it is published only after that confirmation.",
      ],
    },
    {
      heading: "Illustrative material",
      body: [
        "Charts labelled “Illustrative example — not actual performance” explain how verified data will be presented once available. They are schematic and must not be read as any product's actual or projected results.",
      ],
    },
  ],
};

export const CUSTODY = {
  eyebrow: "Transparency",
  title: "Custody and Asset Handling",
  intro:
    "Custody is the single most important structural difference between our offerings. This page states, plainly, who holds assets under each model.",
  sections: [
    {
      heading: "Non-custodial offerings",
      body: [
        "Master Trading: capital stays in your own exchange account. The exchange's copy-trading infrastructure mirrors the master trader's positions; we never touch your funds.",
        "Strategy Trading: strategies execute through API keys created on your own exchange account, with trade permission only — never withdrawal permission. Assets never leave your account.",
      ],
    },
    {
      heading: "Custodial offerings",
      body: [
        "Vault Trading: participating assets are held with the exchange or host platform under the vault's custody arrangement. This means you rely on that venue's custody, solvency and operational controls. Withdrawals follow the vault's published redemption windows and conditions.",
        "Vault Yield: participation is custodial and structured through a promissory token issued against the invested amount. Counterparty and issuer default risk applies and is disclosed in full on the Vault Yield page.",
      ],
    },
    {
      heading: "What we never do",
      bullets: [
        "We never request exchange API keys with withdrawal permission.",
        "We never ask for wallet private keys or seed phrases.",
        "We never accept cash, or handle client money outside the disclosed custodial structures.",
      ],
    },
  ],
};

export const FEES_TERMS = {
  eyebrow: "Transparency",
  title: "Fees and Product Terms",
  intro:
    "Fee schedules and product terms are published only where confirmed with the relevant provider. Unconfirmed terms are labelled as such — we do not publish estimates.",
  sections: [
    {
      heading: "Master Trading",
      body: [
        "Costs arise at the exchange level: the host exchange's copy-trading fees and the master trader's own profit-share terms, shown on each exchange's master page. We publish no additional fee figures until they are confirmed.",
      ],
    },
    {
      heading: "Strategy Trading",
      body: [
        "Strategy subscription or performance terms are disclosed with each strategy's description once confirmed. Any exchange-level trading fees continue to apply in your own account, as set by the exchange.",
      ],
    },
    {
      heading: "Vault Trading and Vault Yield",
      body: [
        "Participation terms — fees, minimums, lock-in windows, maturity and redemption conditions — are published per vault or issuance when confirmed with the operating provider. Where a term is not yet confirmed, the product page says so explicitly.",
      ],
    },
    {
      heading: "Requesting confirmed terms",
      body: [
        `Confirmed, current terms for any product can be requested through the Contact page or at ${SITE.contactEmail}.`,
      ],
    },
  ],
};